'use client'

// Who a post is about, as a face and a name: the market behind a smart tag.
//
// A feed that mixes markets (the homepage) has to say which one each post is
// about, and a cashtag alone doesn't — `$OWDOGGOTOTHEMOON` is derived text.
// The icon and the market's real name come from the same places the rest of
// the site reads them: the AMM asset feed for alkanes, the collection record
// for every other OW market.

import useSwr from 'swr'
import { API_URL } from '@/utils/constants'
import collectionIconUrl from '@/utils/collection-icon-url'
import { OW_NAMESPACE, slugFromTagId } from './collection-tag'
import type { TwetchSmartTag } from './twetch-api'

export interface MarketBadge {
	name: string
	icon: string | null
	/// Where this market lives on the site.
	href: string
}

export interface AlkaneAsset {
	id: string
	name?: string | null
	icon?: string | null
	/// What the pool actually holds. Anyone can mint an alkane and name it
	/// after a famous ticker; this is how much market is behind the name.
	liquidity_usd?: number | null
}

/// One shared read for every alkane badge on the page (SWR dedupes the key).
export const ALKANE_ASSETS_KEY = 'alkanes-amm-assets'
const ALKANE_TTL_MS = 60_000

export async function fetchAlkaneAssets(): Promise<Map<string, AlkaneAsset>> {
	const res = await fetch(`${API_URL}/alkane/assets`)
	if (!res.ok) throw new Error(`alkane/assets ${res.status}`)
	const body = (await res.json()) as { assets?: AlkaneAsset[] }
	return new Map((body.assets ?? []).map((asset) => [asset.id, asset]))
}

interface CollectionRecord {
	name?: string | null
	icon?: string | null
	icon_inscription?: { id?: string | null } | null
}

async function fetchCollection(slug: string): Promise<CollectionRecord> {
	const res = await fetch(`${API_URL}/collection/${encodeURIComponent(slug)}`)
	if (!res.ok) throw new Error(`collection ${res.status}`)
	return (await res.json()) as CollectionRecord
}

/// The market a tag names, or null while it loads (and for a tag no OW market
/// answers for — an unknown market shows nothing rather than a broken chip).
export default function useMarketBadge(tag: TwetchSmartTag | null | undefined): MarketBadge | null {
	const isAlkane = tag?.namespace === 'alkanes'
	const slug = tag?.namespace === OW_NAMESPACE ? slugFromTagId(tag.id) : null
	// Galleries are not collections — `/collection/gallery:<id>` has no record
	// to serve, so asking only buys a console error per post.
	const collectionSlug = slug && !slug.includes(':') ? slug : null

	const { data: assets } = useSwr(isAlkane ? ALKANE_ASSETS_KEY : null, fetchAlkaneAssets, {
		revalidateOnFocus: false,
		dedupingInterval: ALKANE_TTL_MS,
		shouldRetryOnError: false
	})
	const { data: collection } = useSwr(
		collectionSlug ? `alkanes-ow-collection-${collectionSlug}` : null,
		() => fetchCollection(collectionSlug!),
		{ revalidateOnFocus: false, shouldRetryOnError: false }
	)

	if (!tag) return null
	if (isAlkane) {
		const asset = assets?.get(tag.id)
		return asset
			? {
					name: asset.name?.trim() || `$${tag.symbol}`,
					icon: asset.icon ?? null,
					href: `/alkanes/${tag.id}`
			  }
			: null
	}
	if (!slug || !collection) return null
	return {
		name: collection.name?.trim() || slug,
		icon: collectionIconUrl({ ...collection, slug }),
		href: `/collection/${encodeURIComponent(slug)}`
	}
}
